/**
 * WHAT WE SELL — GASES, GRADES, SIZES, SKUs, AND THE TAX THEY CARRY.
 *
 * The catalogue is small, changes rarely, and everything downstream depends on
 * it: a SKU is a gas in a grade in a size, and a price list prices SKUs. So the
 * screen shows the whole shape at once rather than making somebody click
 * through four registers to learn that acetylene has two grades.
 *
 * TAX IS RECORDED HERE AND NOT YET USED (D263). The rates below are kept with
 * the source they came from and an effective date, because a rate with no date
 * is a rate that silently reprices last year. What CTS does NOT do is calculate
 * a statutory invoice from them: the CTS-versus-Tally boundary and the
 * CA-approved matrix are unruled, and the screen says so where a reader would
 * otherwise assume.
 */
import * as React from 'react';
import { useState } from 'react';
import { api, can, type Session } from '../api.js';
import { useAsk, useRun } from '../hooks.js';
import { Banner, Button, Card, Field, Loading, Pill, Register, Select, Text, TextArea, Check, type Column } from '../ui.js';
import { Pager } from '../pager.js';
import { Page } from '../shell.js';
import { count, date, today, words } from '../format.js';
import { screenById } from '../../../src/screens/contracts.js';

const MESSAGES = screenById('catalogue')!.messages;
type Tab = 'products' | 'catalogue' | 'skus' | 'tax' | 'charges' | 'terms';

// 0175 — CTS1's four printed schedules, in the order they print on an offer.
const SCHEDULE_KINDS = ['security', 'additional', 'service', 'damage'] as const;
type ScheduleKind = (typeof SCHEDULE_KINDS)[number];
const SCHEDULE_TITLES: Record<ScheduleKind, string> = {
  security: 'Cylinder security deposit',
  additional: 'Additional charges — accessories and parts',
  service: 'Service charges',
  damage: 'Cylinder damage charges',
};
const SCHEDULE_HINTS: Record<ScheduleKind, string> = {
  security: 'What is held against a cylinder, and on what terms it comes back.',
  additional: 'Valves, caps, rings and the rest — what a customer pays for a part we replace.',
  service: 'Cleaning, painting, testing, preconditioning — work done on a cylinder.',
  damage: 'The damage classes and what each is charged at.',
};

export function Catalogue({ session }: { session: Session }) {
  const [tab, setTab] = useState<Tab>('products');
  return (
    <Page title="Products"
          sub="What we sell: a gas at a purity, sold by Nm³ or kg, with its HSN and GST. Add one in one form; the pieces behind it are under Gases, grades and shells.">
      <div className="segmented">
        <button type="button" className={tab === 'products' ? 'on' : ''} onClick={() => setTab('products')}>Products</button>
        <button type="button" className={tab === 'catalogue' ? 'on' : ''} onClick={() => setTab('catalogue')}>Gases, grades and shells</button>
        <button type="button" className={tab === 'tax' ? 'on' : ''} onClick={() => setTab('tax')}>Tax codes</button>
        <button type="button" className={tab === 'charges' ? 'on' : ''} onClick={() => setTab('charges')}>Charges</button>
        <button type="button" className={tab === 'terms' ? 'on' : ''} onClick={() => setTab('terms')}>Offer terms</button>
      </div>
      {tab === 'products' ? <><AddProductPanel session={session} /><Skus session={session} /></>
        : tab === 'catalogue' ? <Shape session={session} />
        : tab === 'charges' ? <Charges session={session} />
        : tab === 'terms' ? <OfferTerms session={session} />
        : <TaxCodes session={session} />}
    </Page>
  );
}

/**
 * ONE PRODUCT, ONE SENTENCE (the owner, 6 October).
 *
 * *"it should have a drop down of the gas, the grade of it in purity%, then
 * HSN code, GSt on it, sold in nm3 or kg all in one, easly editable and
 * deltable"*.
 *
 * He was being asked to build that from five records across five cards, and
 * was blocked until a cylinder size existed. The parts keep their own tab,
 * because a grade does belong to a gas and somebody will need to close one.
 * This is the way in.
 */
function AddProductPanel({ session }: { session: Session }) {
  const parts = useAsk(() => api.view('catalogue.get', {}), [], MESSAGES);
  // 10 Oct (the owner: "adding a gas, adding a SKU — a mess"): the ONE form is open, first,
  // for anyone who may add; the list of what we sell is right under it.
  const [adding, setAdding] = useState(true);
  const may = can(session, 'catalogue.manage');
  const gases = parts.data?.gases ?? [];

  return (
    <>
      {may && adding ? <AddProduct gases={gases} onDone={() => { setAdding(false); parts.reload(); }} /> : null}
      {may && !adding ? <div className="toolbar"><Button kind="primary" onClick={() => setAdding(true)}>Add another product</Button></div> : null}
    </>
  );
}

/** The one sentence, as one form and one command (catalogue.addProduct). */
function AddProduct({ gases, onDone }: {
  gases: readonly { gasId: number; code: string; name: string; defaultUnit: string }[];
  onDone: () => void;
}) {
  const [known, setKnown] = useState('');
  const [gasCode, setGasCode] = useState(''); const [gasName, setGasName] = useState('');
  const [codeTouched, setCodeTouched] = useState(false);
  const [soldBy, setSoldBy] = useState<'m3' | 'kg'>('m3');
  const [purity, setPurity] = useState(''); const [hsn, setHsn] = useState(''); const [gst, setGst] = useState('18');
  // 12 Oct — what the YARD calls this gas. Without it the product cannot be
  // quoted: a quotation line priced from a product has to reach a gas service
  // name, and the owner met that as a refusal on the quotation screen telling
  // him to go and map it somewhere else.
  const [yardName, setYardName] = useState('');
  const [yardTouched, setYardTouched] = useState(false);
  const inUse = useAsk(() => api.view('gases.list', {}), [], MESSAGES);
  const runner = useRun(MESSAGES);

  function chooseKnown(v: string): void {
    setKnown(v);
    const g = gases.find((x) => String(x.gasId) === v);
    if (g) {
      setGasCode(g.code); setGasName(g.name); setSoldBy(g.defaultUnit === 'kg' ? 'kg' : 'm3'); setCodeTouched(true);
      if (!yardTouched) setYardName(g.name.trim().toLowerCase());
    } else { setGasCode(''); setGasName(''); setCodeTouched(false); if (!yardTouched) setYardName(''); }
  }
  /** Name it and the code follows — nobody should have to invent OXY. */
  function nameChanged(v: string): void {
    setGasName(v);
    if (!codeTouched) setGasCode(v.trim().toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 16));
    if (!yardTouched) setYardName(v.trim().toLowerCase());
  }

  async function submit() {
    const ok = await runner.run(() => api.command('catalogue.addProduct', {
      gasCode: gasCode.trim(), gasName: gasName.trim(), soldBy,
      ...(purity.trim() ? { purityPct: Number(purity) } : {}),
      ...(hsn.trim() ? { hsnCode: hsn.trim() } : {}),
      ...(gst.trim() ? { gstPct: Number(gst) } : {}),
      ...(yardName.trim() ? { yardName: yardName.trim() } : {}),
      effectiveFrom: today(),
    }));
    if (ok) onDone();
  }

  return (
    <Card title="Add a product" sub="oxygen, 99.5%, HSN 28044090, 18% GST, sold by Nm³ — one line, one product">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <div className="grid two">
        <div>
          {/* an empty company is not asked to pick from an empty list */}
          {gases.length > 0 ? (
            <Select name="p-known" label="Gas" value={known} onChange={chooseKnown} placeholder="— a gas we do not sell yet"
                    options={gases.map((g) => ({ value: String(g.gasId), label: `${g.name} (${g.code}) — sold by ${g.defaultUnit === 'kg' ? 'kg' : 'Nm³'}` }))}
                    help="choose one you already sell, or leave this and name a new one below" />
          ) : (
            <p className="muted" style={{ marginTop: 0 }}>
              You do not sell any gas yet. Name the first one — oxygen, acetylene, nitrogen — and it is created with this product.
            </p>
          )}
          {!known ? (
            <>
              <Text name="p-name" label="Gas name" value={gasName} onChange={nameChanged} required autoFocus
                    help="e.g. Oxygen, Medical Oxygen, Acetylene" />
              <Text name="p-code" label="Short code" value={gasCode}
                    onChange={(v) => { setCodeTouched(true); setGasCode(v.toUpperCase()); }}
                    help={codeTouched ? 'how it is written on a challan' : 'made from the name — change it if you want something shorter'} />
              <Select name="p-unit" label="Sold by" value={soldBy} onChange={(v) => setSoldBy(v === 'kg' ? 'kg' : 'm3')}
                      options={[{ value: 'm3', label: 'Nm³ — oxygen, nitrogen, the seamless gases' }, { value: 'kg', label: 'kg — acetylene, CO₂' }]} />
            </>
          ) : (
            <p className="muted">Sold by <strong>{soldBy === 'kg' ? 'kg' : 'Nm³'}</strong> — that belongs to the gas, and every grade of it is sold the same way.</p>
          )}
        </div>
        <div>
          <Text name="p-purity" label="Purity (%)" type="number" value={purity} onChange={setPurity}
                help="what makes this grade — 99.5 and 99.9 are two products at two prices" />
          <Text name="p-hsn" label="HSN code" value={hsn} onChange={setHsn} help="four to eight digits; the tax line is raised under it" />
          <Text name="p-gst" label="GST (%)" type="number" value={gst} onChange={setGst} help="recorded from today; a later change keeps the old rate on old invoices" />
          {/* 12 Oct — without this the product cannot be quoted at all. */}
          <Field label="What the yard calls this gas" name="p-yard"
                 help={(inUse.data?.rows ?? []).length
                   ? 'the name on the cylinders and on the customer’s rate card — pick one already in use, or type a new one'
                   : 'the name that will appear on the cylinders and on a customer’s rate card'}>
            <input id="p-yard" list="p-yard-known" value={yardName} placeholder="e.g. oxygen"
                   onChange={(e: { target: { value: string } }) => { setYardTouched(true); setYardName(e.target.value); }} />
            <datalist id="p-yard-known">
              {(inUse.data?.rows ?? []).map((g) => <option key={g.name} value={g.name} />)}
            </datalist>
          </Field>
        </div>
      </div>
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !gasName.trim() || !gasCode.trim() || !yardName.trim()}>
        {runner.busy ? 'Adding…' : !gasName.trim() ? 'Name the gas first' : !gasCode.trim() ? 'Give the gas a short code'
          : !yardName.trim() ? 'Say what the yard calls it' : 'Add the product'}
      </Button>
    </Card>
  );
}

// ---------------------------------------------------------------- gases, grades, sizes
function Shape({ session }: { session: Session }) {
  const asked = useAsk(() => api.view('catalogue.get', {}), [], MESSAGES);
  const runner = useRun(MESSAGES);
  const [adding, setAdding] = useState<'gas' | 'grade' | 'size' | null>(null);
  const may = can(session, 'catalogue.manage');

  if (asked.loading) return <Card title="The catalogue"><Loading what="the catalogue" /></Card>;
  if (asked.error) return <Card title="The catalogue"><Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} /></Card>;
  const d = asked.data!;
  const done = () => { setAdding(null); asked.reload(); };

  async function deactivate(kind: 'gas' | 'grade' | 'size' | 'sku' | 'taxCode', id: number, reason: string) {
    const ok = await runner.run(() => api.command('catalogue.deactivate', { kind, id, reason }));
    if (ok) asked.reload();
  }

  return (
    <>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      {adding === 'gas' ? <AddGas onDone={done} /> : null}
      {adding === 'grade' ? <AddGrade gases={d.gases} onDone={done} /> : null}
      {adding === 'size' ? <AddSize onDone={done} /> : null}

      <div className="grid two">
        <Card title="Gases" sub="what is in the cylinder"
              actions={may ? <Button small kind="primary" onClick={() => setAdding(adding === 'gas' ? null : 'gas')}>{adding === 'gas' ? 'Cancel' : 'Add a gas'}</Button> : null}>
          <Register
            columns={[
              { key: 'code', head: 'Code', cell: (g: { code: string }) => <strong>{g.code}</strong> },
              { key: 'name', head: 'Name', cell: (g: { name: string }) => g.name },
              { key: 'unit', head: 'Sold by', cell: (g: { defaultUnit: string }) => words(g.defaultUnit) },
              { key: 'grades', head: 'Grades', right: true, cell: (g: { gasId: number }) => count(d.grades.filter((x) => x.gasId === g.gasId).length) },
              { key: 'state', head: '', cell: (g: { active: boolean }) => g.active ? null : <Pill tone="muted">closed</Pill> },
              { key: 'off', head: '', cell: (g: { gasId: number; active: boolean; code: string }) => may && g.active
                  ? <Close what={`gas ${g.code}`} onConfirm={(r) => { void deactivate('gas', g.gasId, r); }} /> : null },
            ]}
            rows={d.gases} empty="No gas is in the catalogue yet." />
        </Card>

        <Card title="Grades" sub="how pure — the same gas sold to different specifications"
              actions={may ? <Button small kind="primary" onClick={() => setAdding(adding === 'grade' ? null : 'grade')}>{adding === 'grade' ? 'Cancel' : 'Add a grade'}</Button> : null}>
          <Register
            columns={[
              { key: 'gas', head: 'Gas', cell: (g: { gasId: number }) => d.gases.find((x) => x.gasId === g.gasId)?.code ?? '—' },
              { key: 'code', head: 'Code', cell: (g: { code: string }) => <strong>{g.code}</strong> },
              { key: 'name', head: 'Name', cell: (g: { name: string; purityText: string | null }) => <>{g.name}{g.purityText ? <div className="muted">{g.purityText}</div> : null}</> },
              { key: 'state', head: '', cell: (g: { active: boolean }) => g.active ? null : <Pill tone="muted">closed</Pill> },
              { key: 'off', head: '', cell: (g: { gradeId: number; active: boolean; code: string }) => may && g.active
                  ? <Close what={`grade ${g.code}`} onConfirm={(r) => { void deactivate('grade', g.gradeId, r); }} /> : null },
            ]}
            rows={d.grades} empty="No grade is recorded." />
        </Card>
      </div>

      <Card title="Shells (cylinder sizes)" sub="water capacity is what the shell is; nominal quantity is what it usually holds of a gas — the yard's list, not the salesman's"
            actions={may ? <Button small kind="primary" onClick={() => setAdding(adding === 'size' ? null : 'size')}>{adding === 'size' ? 'Cancel' : 'Add a size'}</Button> : null}>
        <Register
          columns={[
            { key: 'code', head: 'Code', cell: (z: { code: string }) => <strong>{z.code}</strong> },
            { key: 'name', head: 'Name', cell: (z: { name: string }) => z.name },
            { key: 'water', head: 'Water capacity', right: true, cell: (z: { waterCapacityL: number }) => `${z.waterCapacityL} L` },
            { key: 'nominal', head: 'Usually holds', right: true, cell: (z: { nominalQty: number | null; nominalUnit: string | null }) => z.nominalQty ? `${z.nominalQty} ${z.nominalUnit ?? ''}` : <span className="muted">—</span> },
            { key: 'state', head: '', cell: (z: { active: boolean }) => z.active ? null : <Pill tone="muted">closed</Pill> },
            { key: 'off', head: '', cell: (z: { sizeId: number; active: boolean; code: string }) => may && z.active
                ? <Close what={`size ${z.code}`} onConfirm={(r) => { void deactivate('size', z.sizeId, r); }} /> : null },
          ]}
          rows={d.sizes} empty="No size is recorded." />
      </Card>
    </>
  );
}

/** Closing a catalogue entry asks why, because everything downstream references it. */
function Close({ what, onConfirm }: { what: string; onConfirm: (reason: string) => void }) {
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');
  if (!asking) return <Button small onClick={() => setAsking(true)}>Close</Button>;
  return (
    <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <input type="text" placeholder={`Why close ${what}?`} value={reason}
             onChange={(e: { target: { value: string } }) => setReason(e.target.value)} />
      <Button small kind="danger" disabled={!reason.trim()} onClick={() => { onConfirm(reason.trim()); setAsking(false); }}>Confirm</Button>
      <Button small onClick={() => setAsking(false)}>Keep</Button>
    </span>
  );
}

function AddGas({ onDone }: { onDone: () => void }) {
  const [code, setCode] = useState(''); const [name, setName] = useState(''); const [unit, setUnit] = useState('m3');
  const runner = useRun(MESSAGES);
  async function submit() {
    const ok = await runner.run(() => api.command('catalogue.addGas', { code: code.trim(), name: name.trim(), defaultUnit: unit as 'kg' | 'm3' | 'l' }));
    if (ok) onDone();
  }
  return (
    <Card title="Add a gas">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <div className="grid two">
        <div><Text name="gas-code" label="Code" value={code} onChange={setCode} required autoFocus />
             <Text name="gas-name" label="Name" value={name} onChange={setName} required /></div>
        <div><Select name="gas-unit" label="Sold by" value={unit} onChange={setUnit}
                     options={[{ value: 'm3', label: 'Cubic metres' }, { value: 'kg', label: 'Kilograms' }, { value: 'l', label: 'Litres' }]} /></div>
      </div>
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !code.trim() || !name.trim()}>Add it</Button>
    </Card>
  );
}

function AddGrade({ gases, onDone }: { gases: readonly { gasId: number; code: string; name: string }[]; onDone: () => void }) {
  const [gasId, setGasId] = useState(''); const [code, setCode] = useState('');
  const [name, setName] = useState(''); const [purity, setPurity] = useState('');
  const runner = useRun(MESSAGES);
  async function submit() {
    const ok = await runner.run(() => api.command('catalogue.addGrade', {
      gasId: Number(gasId), code: code.trim(), name: name.trim(), ...(purity.trim() ? { purityText: purity.trim() } : {}),
    }));
    if (ok) onDone();
  }
  return (
    <Card title="Add a grade" sub="a grade belongs to one gas">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <div className="grid two">
        <div><Select name="grade-gas" label="Gas" value={gasId} onChange={setGasId} placeholder="Choose the gas"
                     options={gases.map((g) => ({ value: String(g.gasId), label: `${g.code} — ${g.name}` }))} />
             <Text name="grade-code" label="Code" value={code} onChange={setCode} required /></div>
        <div><Text name="grade-name" label="Name" value={name} onChange={setName} required />
             <Text name="grade-purity" label="Purity" value={purity} onChange={setPurity} help="e.g. 99.5%" /></div>
      </div>
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !gasId || !code.trim() || !name.trim()}>Add it</Button>
    </Card>
  );
}

function AddSize({ onDone }: { onDone: () => void }) {
  const [code, setCode] = useState(''); const [name, setName] = useState('');
  const [water, setWater] = useState(''); const [qty, setQty] = useState(''); const [unit, setUnit] = useState('');
  const runner = useRun(MESSAGES);
  async function submit() {
    const ok = await runner.run(() => api.command('catalogue.addSize', {
      code: code.trim(), name: name.trim(), waterCapacityL: Number(water),
      ...(qty.trim() ? { nominalQty: Number(qty) } : {}),
      ...(unit ? { nominalUnit: unit as 'kg' | 'm3' | 'l' } : {}),
    }));
    if (ok) onDone();
  }
  return (
    <Card title="Add a shell (cylinder size)">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <div className="grid two">
        <div><Text name="size-code" label="Code" value={code} onChange={setCode} required autoFocus />
             <Text name="size-name" label="Name" value={name} onChange={setName} required />
             <Text name="size-water" label="Water capacity (L)" type="number" value={water} onChange={setWater} required /></div>
        <div><Text name="size-qty" label="Usually holds" type="number" value={qty} onChange={setQty} help="leave blank if it varies by gas" />
             <Select name="size-unit" label="In" value={unit} onChange={setUnit} placeholder="—"
                     options={[{ value: 'm3', label: 'Cubic metres' }, { value: 'kg', label: 'Kilograms' }, { value: 'l', label: 'Litres' }]} /></div>
      </div>
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !code.trim() || !name.trim() || !water}>Add it</Button>
    </Card>
  );
}

// ---------------------------------------------------------------- SKUs
function Skus({ session }: { session: Session }) {
  const [q, setQ] = useState(''); const [onlyActive, setOnlyActive] = useState(true);
  const [adding, setAdding] = useState(false);
  const [page, setPage] = useState(1);
  const asked = useAsk(() => api.view('skus.list', { ...(q.trim() ? { q: q.trim() } : {}), ...(onlyActive ? { active: true } : {}), page, pageSize: 50 }), [q, onlyActive, page], MESSAGES);
  const shape = useAsk(() => api.view('catalogue.get', { active: true }), [], MESSAGES);
  const taxes = useAsk(() => api.view('taxcodes.list', {}), [], MESSAGES);
  const may = can(session, 'catalogue.manage');
  // D179 — classifying a SKU is its OWN permission (0137). Changing which HSN something is
  // sold under is an accounting act, and the person who adds a cylinder size is not
  // necessarily the person who may decide what tax it attracts.
  const mayClassify = can(session, 'catalogue.classify');
  const runner = useRun(MESSAGES);
  const [classifying, setClassifying] = useState<number | null>(null);
  const [newTax, setNewTax] = useState('');
  const [whyTax, setWhyTax] = useState('');

  async function classify(skuId: number) {
    const ok = await runner.run(() => api.command('catalogue.classifySku', {
      skuId, taxCodeId: Number(newTax), reason: whyTax.trim(),
    }));
    if (ok) { setClassifying(null); setNewTax(''); setWhyTax(''); asked.reload(); }
  }

  const columns: readonly Column<{ skuId: number; code: string; gasCode: string; gasName: string; gasUnit: string; gradeCode: string; gradeName: string; purityText: string | null; sizeCode: string | null; waterCapacityL: number | null; nominalQty: number | null; nominalUnit: string | null; taxCode: string | null; description: string | null; active: boolean }>[] = [
    { key: 'code', head: 'Product', cell: (r) => <strong>{r.code}</strong> },
    // 0161 — the three facts the owner names a product by: the gas, its purity
    // and the unit it is SOLD in. All three were in the published view and none
    // of them on this screen, so a product saved as "oxygen, 99.5%, per Nm³"
    // came back as a bare code.
    { key: 'gas', head: 'Gas', cell: (r) => <>{r.gasName || r.gasCode}<div className="muted">{r.gasCode}</div></> },
    { key: 'grade', head: 'Grade', cell: (r) => <>{r.gradeName || r.gradeCode}{r.purityText ? <div className="muted">{r.purityText}</div> : null}</> },
    { key: 'soldby', head: 'Sold by', cell: (r) => r.gasUnit === 'm3' ? 'Nm³' : r.gasUnit || <span className="muted">—</span> },
    // A product need not name a container at all; one that does keeps showing it.
    { key: 'size', head: 'Container', cell: (r) => r.sizeCode
        ? <>{r.sizeCode}{r.waterCapacityL ? <span className="muted"> · {r.waterCapacityL} L</span> : null}</>
        : <span className="muted">any</span> },
    { key: 'holds', head: 'Holds', right: true, cell: (r) => r.nominalQty ? `${r.nominalQty} ${r.nominalUnit ?? ''}` : <span className="muted">—</span> },
    { key: 'tax', head: 'Tax code', cell: (r) => {
        // A SKU WITH NO TAX CODE IS NOT A BLANK, IT IS A BLOCKER: the pricing cutover refuses
        // a company whose mapped SKUs have no classification, so "none" is work rather than a
        // missing adornment. Until 0137 it was also a DEAD END — the code could be set at
        // creation and never afterwards — which is why this control exists at all.
        if (classifying === r.skuId) {
          return (
            <div className="stack">
              <Select name={`tax-${r.skuId}`} label="Tax code" value={newTax} onChange={setNewTax}
                      placeholder="Choose a tax code"
                      options={(taxes.data?.rows ?? []).map((t: { taxCodeId: number; code: string; kind: string }) =>
                        ({ value: String(t.taxCodeId), label: `${t.code} (${t.kind.toUpperCase()})` }))} />
              <TextArea name={`taxwhy-${r.skuId}`} label="Why" value={whyTax} onChange={setWhyTax}
                        placeholder="the notification, the circular, the accountant's advice" />
              <div className="toolbar">
                <Button kind="primary" onClick={() => { void classify(r.skuId); }}
                        disabled={runner.busy || !newTax || !whyTax.trim()}>
                  {runner.busy ? 'Recording…' : 'Record the classification'}
                </Button>
                <Button kind="ghost" onClick={() => { setClassifying(null); setWhyTax(''); }}>Leave it</Button>
              </div>
            </div>
          );
        }
        return (
          <>
            {r.taxCode ?? <span className="muted">none</span>}
            {mayClassify && r.active
              ? <> <a href="#" onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); setClassifying(r.skuId); setNewTax(''); setWhyTax(''); }}>{r.taxCode ? 'reclassify' : 'classify'}</a></>
              : null}
          </>
        );
      } },
    { key: 'state', head: '', cell: (r) => r.active ? null : <Pill tone="muted">closed</Pill> },
  ];

  return (
    <>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <div className="toolbar">
        <div className="field grow">
          <label htmlFor="q">Search</label>
          <input id="q" type="text" value={q} placeholder="Code or description" onChange={(e: { target: { value: string } }) => setQ(e.target.value)} />
        </div>
        <Check name="sku-active" checked={onlyActive} onChange={setOnlyActive} label="Only the ones we still sell" />
        {may ? <Button small onClick={() => setAdding((v) => !v)}>{adding ? 'Cancel' : 'Add a product for one shell only (advanced)'}</Button> : null}
      </div>

      {adding && shape.data && taxes.data ? (
        <AddSku gases={shape.data.gases} grades={shape.data.grades} sizes={shape.data.sizes} taxes={taxes.data.rows}
                onDone={() => { setAdding(false); asked.reload(); }} />
      ) : null}

      <Card title="What we sell" sub="each product is a gas at a grade with its HSN; a price list prices these, per Nm³ or per kg">
        {asked.loading ? <Loading what="the products" /> : asked.error ? <Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} />
          : (<>
              <Register columns={columns} rows={asked.data!.rows} empty="Nothing is on sale yet — add a product above."
 />
              <Pager page={asked.data!.page} onPage={setPage} />
            </>)}
      </Card>
    </>
  );
}

function AddSku({ gases, grades, sizes, taxes, onDone }: {
  gases: readonly { gasId: number; code: string; name: string }[];
  grades: readonly { gradeId: number; gasId: number; code: string; name: string }[];
  sizes: readonly { sizeId: number; code: string; name: string }[];
  taxes: readonly { taxCodeId: number; code: string; description: string }[];
  onDone: () => void;
}) {
  const [code, setCode] = useState(''); const [gasId, setGasId] = useState('');
  const [gradeId, setGradeId] = useState(''); const [sizeId, setSizeId] = useState('');
  const [taxCodeId, setTaxCodeId] = useState(''); const [description, setDescription] = useState('');
  const runner = useRun(MESSAGES);

  async function submit() {
    const ok = await runner.run(() => api.command('catalogue.addSku', {
      code: code.trim(), gasId: Number(gasId), gradeId: Number(gradeId), sizeId: Number(sizeId),
      ...(taxCodeId ? { taxCodeId: Number(taxCodeId) } : {}),
      ...(description.trim() ? { description: description.trim() } : {}),
    }));
    if (ok) onDone();
  }

  return (
    <Card title="Add a product for one shell only" sub="advanced — the ordinary product is a gas at a grade, with no shell; this ties one to a cylinder size">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <div className="grid two">
        <div>
          <Text name="sku-code" label="Code" value={code} onChange={setCode} required autoFocus />
          <Select name="sku-gas" label="Gas" value={gasId} onChange={(v) => { setGasId(v); setGradeId(''); }} placeholder="Choose the gas"
                  options={gases.map((g) => ({ value: String(g.gasId), label: `${g.code} — ${g.name}` }))} />
          <Select name="sku-grade" label="Grade" value={gradeId} onChange={setGradeId} placeholder={gasId ? 'Choose the grade' : 'Choose the gas first'}
                  options={grades.filter((g) => String(g.gasId) === gasId).map((g) => ({ value: String(g.gradeId), label: `${g.code} — ${g.name}` }))} />
        </div>
        <div>
          <Select name="sku-size" label="Size" value={sizeId} onChange={setSizeId} placeholder="Choose the size"
                  options={sizes.map((z) => ({ value: String(z.sizeId), label: `${z.code} — ${z.name}` }))} />
          <Select name="sku-tax" label="Tax code" value={taxCodeId} onChange={setTaxCodeId} placeholder="None yet"
                  options={taxes.map((t) => ({ value: String(t.taxCodeId), label: `${t.code} — ${t.description}` }))} />
          <Text name="sku-description" label="Description" value={description} onChange={setDescription} />
        </div>
      </div>
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !code.trim() || !gasId || !gradeId || !sizeId}>Add it</Button>
    </Card>
  );
}

// ---------------------------------------------------------------- tax codes
function TaxCodes({ session }: { session: Session }) {
  const [on, setOn] = useState(today());
  const asked = useAsk(() => api.view('taxcodes.list', { on }), [on], MESSAGES);
  const [adding, setAdding] = useState(false);
  const [rating, setRating] = useState<number | null>(null);
  const may = can(session, 'tax.manage');

  return (
    <>
      <Banner kind="info" title="These rates are RECORDED here. CTS does not yet calculate a statutory invoice from them."
              detail="The boundary between CTS and Tally, and the CA-approved rate matrix, are not settled. Until they are, what this company issues is a proforma and a delivery memo — never a tax invoice — and these codes are kept so that the day the ruling comes, the history is already here with the source each rate came from." />

      <div className="toolbar">
        <Text name="on" label="Rates in force on" type="date" value={on} onChange={setOn} />
        {may ? <Button kind="primary" onClick={() => setAdding((v) => !v)}>{adding ? 'Cancel' : 'Add a tax code'}</Button> : null}
      </div>

      {adding ? <AddTaxCode onDone={() => { setAdding(false); asked.reload(); }} /> : null}

      <Card title="Tax codes" sub="an HSN or SAC code, and the rate in force on the date above">
        {asked.loading ? <Loading what="the tax codes" /> : asked.error ? <Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} /> : (
          <>
            {asked.data!.withoutRate.length ? (
              <Banner kind="warn" title={`${count(asked.data!.withoutRate.length)} code(s) have NO rate on ${date(on)}.`}
                      detail={`${asked.data!.withoutRate.join(', ')} — no rate is not the same as a rate of zero, and nothing here will pretend it is.`} />
            ) : null}
            <Register
              columns={[
                { key: 'code', head: 'Code', cell: (t: { code: string; kind: string }) => <><strong>{t.code}</strong><div className="muted">{t.kind.toUpperCase()}</div></> },
                { key: 'desc', head: 'What it covers', cell: (t: { description: string }) => t.description },
                { key: 'rate', head: 'Rate', right: true, cell: (t: { ratePct: number | null; cessPct: number | null }) => t.ratePct === null
                    ? <Pill tone="danger">no rate on this date</Pill>
                    : <>{t.ratePct}%{t.cessPct ? <span className="muted"> + {t.cessPct}% cess</span> : null}</> },
                { key: 'from', head: 'In force', cell: (t: { rateFrom: string | null; rateTo: string | null }) => t.rateFrom
                    ? <>{date(t.rateFrom)}{t.rateTo ? ` — ${date(t.rateTo)}` : ' onwards'}</> : <span className="muted">—</span> },
                { key: 'src', head: 'Source', cell: (t: { rateSource: string | null }) => t.rateSource ?? <span className="muted">—</span> },
                { key: 'act', head: '', cell: (t: { taxCodeId: number }) => may
                    ? <Button small onClick={() => setRating((v) => v === t.taxCodeId ? null : t.taxCodeId)}>{rating === t.taxCodeId ? 'Close' : 'Record a rate'}</Button> : null },
              ]}
              rows={asked.data!.rows} empty="No tax code is recorded." asOf={asked.data!.on} />
          </>
        )}
      </Card>

      {rating !== null ? <RecordRate taxCodeId={rating} onDone={() => { setRating(null); asked.reload(); }} /> : null}
    </>
  );
}

function AddTaxCode({ onDone }: { onDone: () => void }) {
  const [code, setCode] = useState(''); const [kind, setKind] = useState('hsn'); const [description, setDescription] = useState('');
  const runner = useRun(MESSAGES);
  async function submit() {
    const ok = await runner.run(() => api.command('tax.addCode', { code: code.trim(), kind: kind as 'hsn' | 'sac', description: description.trim() }));
    if (ok) onDone();
  }
  return (
    <Card title="Add a tax code">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <div className="grid two">
        <div><Text name="tax-code" label="Code" value={code} onChange={setCode} required autoFocus help="four to eight characters" />
             <Select name="tax-kind" label="Kind" value={kind} onChange={setKind}
                     options={[{ value: 'hsn', label: 'HSN — goods' }, { value: 'sac', label: 'SAC — services' }]} /></div>
        <div><Text name="tax-desc" label="What it covers" value={description} onChange={setDescription} required /></div>
      </div>
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !code.trim() || !description.trim()}>Add it</Button>
    </Card>
  );
}

function RecordRate({ taxCodeId, onDone }: { taxCodeId: number; onDone: () => void }) {
  const [ratePct, setRatePct] = useState(''); const [cessPct, setCessPct] = useState('');
  const [from, setFrom] = useState(today()); const [to, setTo] = useState(''); const [source, setSource] = useState('');
  const runner = useRun(MESSAGES);
  async function submit() {
    const ok = await runner.run(() => api.command('tax.recordRate', {
      taxCodeId, ratePct: Number(ratePct), effectiveFrom: from, source: source.trim(),
      ...(cessPct.trim() ? { cessPct: Number(cessPct) } : {}),
      ...(to ? { effectiveTo: to } : {}),
    }));
    if (ok) onDone();
  }
  return (
    <Card title="Record a rate" sub="a rate without a date silently reprices last year — the date and the source are both required">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <div className="grid two">
        <div><Text name="rate-pct" label="Rate %" type="number" value={ratePct} onChange={setRatePct} required autoFocus />
             <Text name="cess-pct" label="Cess %" type="number" value={cessPct} onChange={setCessPct} /></div>
        <div><Text name="rate-from" label="Effective from" type="date" value={from} onChange={setFrom} required />
             <Text name="rate-to" label="Effective to" type="date" value={to} onChange={setTo} help="leave blank while it is the current rate" /></div>
      </div>
      <TextArea name="rate-source" label="Where this rate comes from" value={source} onChange={setSource}
                help="the notification, circular or advice — so the next person can check it" />
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !ratePct || !from || source.trim().length < 3}>Record it</Button>
    </Card>
  );
}

/**
 * THE PRINTED SCHEDULES (0175) — the deposit, the parts, the services and the
 * damage classes that print beneath the rates on every offer.
 *
 * The owner, 11 October: "what about additonal charges and service charges?"
 * They are kept ONCE here and copied onto an offer when it is drafted; the
 * offer keeps its own copy, so correcting a rate here never rewrites an offer
 * already sent. The rate is WORDS — "Rs 900/ Pc + GST" — because that is what
 * a buyer reads; nothing computes with it.
 */
function Charges({ session }: { session: Session }) {
  const asked = useAsk(() => api.view('charges.list', {}), [], MESSAGES);
  const may = can(session, 'catalogue.manage');
  if (asked.loading) return <Loading what="the schedules" />;
  if (asked.error) return <Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} />;
  const rows = asked.data?.rows ?? [];
  return (
    <>
      <Card>
        <p className="muted" style={{ margin: 0 }}>
          These print on every new offer, where they can be unticked or changed for that offer alone.
          A rate here is the words the buyer reads, not a figure the system bills with.
        </p>
      </Card>
      {SCHEDULE_KINDS.map((kind) => (
        <ChargeTable key={kind} kind={kind} may={may}
                     rows={rows.filter((r) => r.kind === kind).map((r) => ({ item: r.item, rate: r.printedRate, hsn: r.hsn ?? '', gstPct: r.gstPct === null ? '' : String(r.gstPct) }))}
                     onSaved={() => asked.reload()} />
      ))}
    </>
  );
}

function ChargeTable({ kind, rows, may, onSaved }: {
  kind: ScheduleKind; rows: { item: string; rate: string; hsn: string; gstPct: string }[]; may: boolean; onSaved: () => void;
}) {
  const [draft, setDraft] = useState(rows);
  const [dirty, setDirty] = useState(false);
  const runner = useRun(MESSAGES);
  const set = (i: number, patch: Partial<{ item: string; rate: string; hsn: string; gstPct: string }>) => {
    setDirty(true); setDraft((o) => o.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  };
  async function save() {
    const ok = await runner.run(() => api.command('catalogue.saveCharges', {
      kind,
      rows: draft.filter((r) => r.item.trim() && r.rate.trim()).map((r) => ({
        item: r.item.trim(), rate: r.rate.trim(),
        ...(r.hsn.trim() ? { hsn: r.hsn.trim() } : {}),
        ...(r.gstPct.trim() ? { gstPct: Number(r.gstPct) } : {}),
      })).map((r) => ({ item: r.item, printedRate: r.rate, ...(r.hsn ? { hsn: r.hsn } : {}), ...(r.gstPct === undefined ? {} : { gstPct: r.gstPct }) })),
    }));
    if (ok) { setDirty(false); onSaved(); }
  }
  return (
    <Card title={SCHEDULE_TITLES[kind]} sub={SCHEDULE_HINTS[kind]}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <table className="register sched-table">
        <thead><tr><th>Item</th><th>Rate, as it prints</th><th>HSN / SAC</th><th>GST %</th>{may ? <th /> : null}</tr></thead>
        <tbody>
          {draft.length === 0 ? <tr><td colSpan={may ? 5 : 4} className="muted">Nothing on this schedule — an offer prints it only when it has rows.</td></tr> : null}
          {draft.map((r, i) => (
            <tr key={i}>
              <td>{may ? <input value={r.item} aria-label="Item" onChange={(e: { target: { value: string } }) => set(i, { item: e.target.value })} /> : r.item}</td>
              <td>{may ? <input value={r.rate} aria-label="Rate" placeholder="Rs 900/ Pc + GST" onChange={(e: { target: { value: string } }) => set(i, { rate: e.target.value })} /> : r.rate}</td>
              <td>{may ? <input value={r.hsn} aria-label="HSN or SAC" onChange={(e: { target: { value: string } }) => set(i, { hsn: e.target.value })} /> : (r.hsn || '—')}</td>
              <td>{may ? <input value={r.gstPct} type="number" aria-label="GST per cent" onChange={(e: { target: { value: string } }) => set(i, { gstPct: e.target.value })} /> : (r.gstPct ? `${r.gstPct}%` : '—')}</td>
              {may ? <td><Button small kind="danger" onClick={() => { setDirty(true); setDraft((o) => o.filter((_, k) => k !== i)); }}>×</Button></td> : null}
            </tr>
          ))}
        </tbody>
      </table>
      {may ? (
        <div className="toolbar">
          <Button small onClick={() => { setDirty(true); setDraft((o) => [...o, { item: '', rate: '', hsn: '', gstPct: '' }]); }}>+ Add a row</Button>
          <Button kind="primary" small disabled={!dirty || runner.busy} onClick={() => { void save(); }}>{runner.busy ? 'Saving…' : 'Save this schedule'}</Button>
        </div>
      ) : null}
    </Card>
  );
}

/**
 * THE STANDING WORDS OF AN OFFER (0176) — the numbered terms of supply, the
 * penalty for late payment, the cost-bill and toll-fill notes, the closing
 * line. Seeded with the company's own, from the offer it already sends.
 *
 * They are edited here and copied onto each offer when it is drafted, so
 * rewording a clause never rewrites an offer already sent.
 */
const TEXT_KINDS = ['term', 'penalty', 'cost_bill', 'closing'] as const;
type TextKind = (typeof TEXT_KINDS)[number];
const TEXT_TITLES: Record<TextKind, string> = {
  term: 'Terms & conditions of supply',
  penalty: 'Penalty for late payment',
  cost_bill: 'Cost-bill & damage notes',
  closing: 'The closing line',
};
const TEXT_HINTS: Record<TextKind, string> = {
  term: 'The numbered clauses, in the order they print.',
  penalty: 'The interest clause, printed in its own box at the end.',
  cost_bill: 'What prints in the cost-bill card: the damage note first, the toll-fill note second.',
  closing: 'The sentence above the signatures.',
};

function OfferTerms({ session }: { session: Session }) {
  const asked = useAsk(() => api.view('offertext.list', {}), [], MESSAGES);
  const may = can(session, 'catalogue.manage');
  if (asked.loading) return <Loading what="the offer's words" />;
  if (asked.error) return <Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} />;
  const rows = asked.data?.rows ?? [];
  return (
    <>
      <Card>
        <p className="muted" style={{ margin: 0 }}>
          Every offer prints these. An offer takes its own copy when it is drafted, so changing a clause
          here never rewrites one already sent.
        </p>
      </Card>
      {TEXT_KINDS.map((kind) => (
        <TextList key={kind} kind={kind} may={may}
                  rows={rows.filter((r) => r.kind === kind).map((r) => ({ title: r.title, body: r.body }))}
                  onSaved={() => asked.reload()} />
      ))}
    </>
  );
}

function TextList({ kind, rows, may, onSaved }: {
  kind: TextKind; rows: { title: string; body: string }[]; may: boolean; onSaved: () => void;
}) {
  const [draft, setDraft] = useState(rows);
  const [dirty, setDirty] = useState(false);
  const runner = useRun(MESSAGES);
  const set = (i: number, patch: Partial<{ title: string; body: string }>) => {
    setDirty(true); setDraft((o) => o.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  };
  async function save() {
    const ok = await runner.run(() => api.command('catalogue.saveOfferText', {
      kind, rows: draft.filter((r) => r.title.trim() && r.body.trim()).map((r) => ({ title: r.title.trim(), body: r.body.trim() })),
    }));
    if (ok) { setDirty(false); onSaved(); }
  }
  return (
    <Card title={TEXT_TITLES[kind]} sub={TEXT_HINTS[kind]}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      {draft.length === 0 ? <p className="muted">Nothing here — an offer prints this section only when it has words.</p> : null}
      {draft.map((r, i) => (
        <div key={i} className="clause">
          <div className="clause-head">
            <span className="clause-n">{String(i + 1).padStart(2, '0')}</span>
            {may ? <input value={r.title} aria-label="Heading" onChange={(e: { target: { value: string } }) => set(i, { title: e.target.value })} />
                 : <b>{r.title}</b>}
            {may ? <Button small kind="danger" onClick={() => { setDirty(true); setDraft((o) => o.filter((_, k) => k !== i)); }}>×</Button> : null}
          </div>
          {may ? <textarea rows={3} value={r.body} aria-label="Words" onChange={(e: { target: { value: string } }) => set(i, { body: e.target.value })} />
               : <p className="muted">{r.body}</p>}
        </div>
      ))}
      {may ? (
        <div className="toolbar">
          <Button small onClick={() => { setDirty(true); setDraft((o) => [...o, { title: '', body: '' }]); }}>+ Add a clause</Button>
          <Button kind="primary" small disabled={!dirty || runner.busy} onClick={() => { void save(); }}>{runner.busy ? 'Saving…' : 'Save these words'}</Button>
        </div>
      ) : null}
    </Card>
  );
}
